import { DeviceRegistry } from './deviceRegistry';
import { LoggerBridge, VOICE_CAPTURE_CONSTRAINTS } from './shared';

// A microphone stream granted by a click (for example the iframe "talk" button) is kept for a short
// while, so the capture that starts next can use it instead of calling getUserMedia again: a second
// request would lose the click's user activation, and on iOS it ends the first capture.
const PERMISSION_STREAM_TTL_MS = 30000;

let permissionStream: MediaStream | null = null;
let permissionStreamTimer: number | null = null;

export function takePermissionStream(requestedMicId: string | null): MediaStream | null {
    const stream = permissionStream;
    permissionStream = null;
    if (permissionStreamTimer !== null) {
        clearTimeout(permissionStreamTimer);
        permissionStreamTimer = null;
    }

    const actualMicId = stream?.getAudioTracks()[0]?.getSettings?.().deviceId;
    if (stream && requestedMicId && actualMicId && requestedMicId !== actualMicId) {
        stream.getTracks().forEach(track => track.stop());
        return null;
    }

    return stream;
}

export async function checkMicrophoneAccess(registry: DeviceRegistry, logger: LoggerBridge): Promise<boolean> {
    const state = await registry.checkMicPermission();
    switch (state) {
        case 'granted':
            await logger.info('Microphone access already granted');
            return true;
        case 'prompt':
            await logger.info('Microphone access needs to be requested');
            return false;
        case 'denied':
            await logger.warning('Microphone access denied');
            return false;
        default:
            await logger.error('Error checking microphone access', 'permissions.query unavailable or failed');
            return false;
    }
}

export async function requestMicrophoneAccess(logger: LoggerBridge): Promise<boolean> {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: VOICE_CAPTURE_CONSTRAINTS, video: false });
        permissionStream?.getTracks().forEach(track => track.stop());
        permissionStream = stream;
        if (permissionStreamTimer !== null) clearTimeout(permissionStreamTimer);
        permissionStreamTimer = window.setTimeout(() => {
            permissionStream?.getTracks().forEach(track => track.stop());
            permissionStream = null;
            permissionStreamTimer = null;
        }, PERMISSION_STREAM_TTL_MS);
        await logger.info('Microphone access granted by user gesture');
        return true;
    } catch (error) {
        await logger.warning('Microphone access request failed', { error: String(error) });
        return false;
    }
}
